import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, Download, Share2, Receipt, ArrowLeft, Clock, CreditCard, Smartphone } from 'lucide-react';
import type { Order, OrderItem, Photo, Event, Payment } from '@/types/schema';
import { getApiBaseUrl } from '@/config/production';
import { SessionManager } from '@/utils/sessionManager';

const API_BASE = getApiBaseUrl();


interface OrderItemWithDetails extends OrderItem {
  photo: Photo & {
    event?: Event;
  };
}

interface OrderWithDetails extends Order {
  items: OrderItemWithDetails[];
  payment?: Payment;
}

export default function OrderConfirmation() {
  const { orderId } = useParams<{ orderId: string }>();
  const [, setLocation] = useLocation();
  const [order, setOrder] = useState<OrderWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloadingItems, setDownloadingItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!orderId) {
      setLocation('/orders');
      return;
    }
    
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/orders/${orderId}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Pedido não encontrado');
        }
        throw new Error('Erro ao carregar pedido');
      }
      
      const orderData = await response.json();
      setOrder(orderData);
    } catch (error) {
      console.error('Erro ao carregar pedido:', error);
      console.error('Erro:', error instanceof Error ? error.message : 'Não foi possível carregar o pedido.');
      setLocation('/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (itemId: string, photoUrl: string, fileName: string) => {
    try {
      setDownloadingItems(prev => new Set(prev).add(itemId));
      
      // Obter token de autenticação
      const token = SessionManager.getToken();
      if (!token) {
        throw new Error('Usuário não autenticado');
      }
      
      // Gerar link de download seguro
      const response = await fetch(`${API_BASE}/api/orders/${orderId}/download/${itemId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Erro ao gerar link de download');
      
      const { downloadUrl } = await response.json();
      
      // Iniciar download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Download iniciado: O download da foto foi iniciado.');
      
    } catch (error) {
      console.error('Erro no download:', error);
      console.error('Erro no download: Não foi possível baixar a foto. Tente novamente.');
    } finally {
      setDownloadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleDownloadAll = async () => {
    if (!order) return;
    
    try {
      // Obter token de autenticação
      const token = SessionManager.getToken();
      if (!token) {
        throw new Error('Usuário não autenticado');
      }
      
      const response = await fetch(`${API_BASE}/api/orders/${orderId}/download-all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Erro ao gerar arquivo ZIP');
      
      const { downloadUrl } = await response.json();
      
      // Iniciar download do ZIP
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `fotos-pedido-${orderId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Download iniciado: O download de todas as fotos foi iniciado.');
      
    } catch (error) {
      console.error('Erro no download:', error);
      console.error('Erro no download: Não foi possível baixar as fotos. Tente novamente.');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Pedido #${orderId}`,
          text: 'Confira meu pedido de fotos!',
          url: window.location.href,
        });
      } catch (error) {
        console.log('Compartilhamento cancelado');
      }
    } else {
      // Fallback: copiar URL para clipboard
      navigator.clipboard.writeText(window.location.href);
      console.log('Link copiado! O link do pedido foi copiado para a área de transferência.');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: 'Pendente', variant: 'secondary' as const },
      processing: { label: 'Processando', variant: 'default' as const },
      completed: { label: 'Concluído', variant: 'default' as const },
      cancelled: { label: 'Cancelado', variant: 'destructive' as const },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'credit_card':
        return <CreditCard className="w-4 h-4" />;
      case 'pix':
        return <Smartphone className="w-4 h-4" />;
      default:
        return <CreditCard className="w-4 h-4" />;
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'credit_card':
        return 'Cartão de Crédito';
      case 'pix':
        return 'PIX';
      default:
        return 'Não informado';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-64 mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            </div>
            <div>
              <Card>
                <CardContent className="p-4 space-y-4">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Pedido não encontrado</h1>
        <Button onClick={() => setLocation('/orders')}>Ver Meus Pedidos</Button>
      </div>
    );
  }

  const isCompleted = order.status === 'completed';
  const isPending = order.status === 'pending';
  const totalItems = order.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            {isCompleted ? 'Pedido Concluído!' : isPending ? 'Pedido Recebido!' : 'Status do Pedido'}
          </h1>
          <p className="text-muted-foreground">
            {isCompleted 
              ? 'Suas fotos estão prontas para download'
              : isPending 
              ? 'Estamos processando seu pagamento'
              : 'Acompanhe o status do seu pedido'
            }
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Detalhes do Pedido */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informações do Pedido */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Pedido #{orderId}</span>
                  {getStatusBadge(order.status)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Data do Pedido</p>
                    <p className="font-medium">{new Date(order.createdAt).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total de Itens</p>
                    <p className="font-medium">{totalItems} {totalItems === 1 ? 'foto' : 'fotos'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Valor Total</p>
                    <p className="font-medium">R$ {parseFloat(order.totalAmount).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Método de Pagamento</p>
                    <div className="flex items-center gap-2">
                      {getPaymentMethodIcon(order.payment?.method || '')}
                      <span className="font-medium">{getPaymentMethodLabel(order.payment?.method || '')}</span>
                    </div>
                  </div>
                </div>
                
                {isPending && order.payment?.method === 'pix' && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-800 mb-2">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">Aguardando Pagamento PIX</span>
                    </div>
                    <p className="text-sm text-yellow-700">
                      Seu pedido será processado assim que o pagamento for confirmado.
                      O PIX expira em 30 minutos.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lista de Fotos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Suas Fotos</span>
                  {isCompleted && order.items.length > 1 && (
                    <Button onClick={handleDownloadAll} variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Baixar Todas
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {order.items.map((item) => {
                  const isDownloading = downloadingItems.has(item.id);
                  const fileName = `foto-${item.photo.id}-${item.photo.event?.title?.replace(/[^a-zA-Z0-9]/g, '-') || 'evento'}.jpg`;
                  
                  return (
                    <div key={item.id} className="flex gap-4 p-4 border rounded-lg">
                      <img
                        src={item.photo.storageUrl}
                        alt={`Foto do evento ${item.photo.event?.title}`}
                        className="w-20 h-20 object-cover rounded"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">
                          {item.photo.event?.title || 'Evento'}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Resolução: {item.photo.resolution}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <span>Qtd: {item.quantity || 1} × R$ {parseFloat(item.unitPrice).toFixed(2)}</span>
                          </div>
                          <div className="font-semibold">
                            R$ {(parseFloat(item.unitPrice) * (item.quantity || 1)).toFixed(2)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        {isCompleted ? (
                          <Button
                            onClick={() => handleDownload(item.id, item.photo.storageUrl, fileName)}
                            disabled={isDownloading}
                            size="sm"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {isDownloading ? 'Baixando...' : 'Download'}
                          </Button>
                        ) : (
                          <Button disabled size="sm" variant="outline">
                            <Clock className="w-4 h-4 mr-2" />
                            Aguardando
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Resumo */}
            <Card>
              <CardHeader>
                <CardTitle>Resumo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal ({totalItems} {totalItems === 1 ? 'item' : 'itens'})</span>
                    <span>R$ {parseFloat(order.totalAmount).toFixed(2)}</span>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex justify-between font-semibold">
                    <span>Total</span>
                    <span>R$ {parseFloat(order.totalAmount).toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ações */}
            <Card>
              <CardContent className="pt-6 space-y-3">
                <Button onClick={handleShare} variant="outline" className="w-full">
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartilhar
                </Button>
                
                <Button onClick={() => setLocation('/orders')} variant="outline" className="w-full">
                  <Receipt className="w-4 h-4 mr-2" />
                  Meus Pedidos
                </Button>
                
                <Button onClick={() => setLocation('/events')} variant="outline" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Continuar Comprando
                </Button>
              </CardContent>
            </Card>

            {/* Informações de Suporte */}
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-2">Precisa de Ajuda?</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>• Downloads disponíveis por 30 dias</p>
                  <p>• Suporte: suporte@fotup.com</p>
                  <p>• WhatsApp: (11) 99999-9999</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}